/**
 * huddleContainer.component.tsx
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
**/

//import bluebird from "bluebird";
import * as React from "react";
import { useBlocker } from "react-router-dom";
import OT from "@opentok/client";


import { GroupNavContext } from "../chat/groupNav.context";

import { Dialog } from "../components/dialog.component";
import { ErrorSnackBar } from "../components/errorSnackBar.component";
import { WelcomeMatComponent } from "./welcomeMat.component";
import { SessionComponent } from "./session.component";

import { Membership } from "../models/auth";
import { GroupMembership } from "../models/group";
import { authServiceFactory } from "../services/auth.service";
import { conferenceServiceFactory } from "../services/conferencing.service";
import { groupServiceFactory } from "../services/group.service";

interface State {
  loading: boolean,
  joining: boolean,
  errMsg: string,
  enableAudio: boolean,
  enableVideo: boolean,
  sessionID: string,
  sessionToken: string,
  session: OT.Session | null,
  fullCSS: string,
  videoSrcID: string,
  audioSrcID: string
}

interface Props {
  groupMembership: GroupMembership
}

/*interface GroupComponentParams {
  groupID: string
}*/

export class HuddleContainer extends React.Component<Props, State> {
  static contextType = GroupNavContext;
  declare context: React.ContextType<typeof GroupNavContext>;

  private leaving: boolean;

  constructor(props: Props) {
    super(props);
    this.leaving = false;

    this.joinRoom = this.joinRoom.bind(this);
    this.toggleAudio = this.toggleAudio.bind(this);
    this.toggleVideo = this.toggleVideo.bind(this);
    this.toggleFullscreen = this.toggleFullscreen.bind(this);
    this.leaveRoom = this.leaveRoom.bind(this);
    this.onPublisherDestroyed = this.onPublisherDestroyed.bind(this);
    this.onRoomError = this.onRoomError.bind(this);
    this.setVideoSource = this.setVideoSource.bind(this);
    this.setAudioSource = this.setAudioSource.bind(this);

    this.state = {
      loading: false,
      joining: false,
      errMsg: "",
      enableAudio: true,
      enableVideo: true,
      sessionID: "",
      sessionToken: "",
      session: null,
      fullCSS: "",
      videoSrcID: "",
      audioSrcID: ""
    };
  }

  componentDidMount(): void {
    this.initGroup();
  }

  componentWillUnmount(): void {
    if (this.state.session) {
      this.state.session?.disconnect();

      // TODO: this causes state update errors, changes url to /group
      //(this.context as GroupNavContext).leaveHuddle();
    }
  }

  private async initGroup() {
    try {
      const room = await conferenceServiceFactory().getRoom(
          this.props.groupMembership.accountID, this.props.groupMembership.groupID);
      if (!room) { return; }

      console.log("existing room:", room);
      this.setState({sessionID: room.sessionID});
    } catch (err) {
      console.error("Error getting room info:", err);
      this.setState({errMsg: (err as Error).message});
    } finally {
      this.setState({loading: false});
    }
  }

  private async joinRoom(isRouted: boolean, notifyMembers: Array<Membership> | undefined, videoSrcID: string, audioSrcID: string): Promise<void> {
    try {
      if (this.state.joining) { return; }
      this.setState({joining: true, videoSrcID: videoSrcID, audioSrcID: audioSrcID});
      const notifyUserIDs = notifyMembers && notifyMembers.map(m => m.userID);
      const groupService = groupServiceFactory();
      const roomData = await conferenceServiceFactory().joinRoom(
          this.props.groupMembership.accountID, this.props.groupMembership.groupID,
          groupService.deviceID, isRouted, notifyUserIDs);
      if (!roomData) {
        this.setState({errMsg: "Couldn't join Huddle"});
        return;
      }
      console.log("joining session:", roomData.sessionID);
      const session = OT.initSession(authServiceFactory().OPENTOK_API_KEY, roomData.sessionID);


      this.context.didJoinHuddle(this.props.groupMembership.groupID);
      this.setState({
        sessionID: roomData.sessionID,
        sessionToken: roomData.token,
        session: session
      });

    } catch (err) {
      console.error("Error joining room:", err);

      if ((err as Error).name === "OT_NOT_CONNECTED") {
          this.setState({errMsg: "Failed to connect. Please check your connection and try connecting again."});
      } else {
          this.setState({errMsg: (err as Error).message});
      }
    } finally {
      this.setState({joining: false});
    }
  }

  toggleAudio(): void {
    this.setState({enableAudio: !this.state.enableAudio});
  }
  toggleVideo(): void {
    this.setState({enableVideo: !this.state.enableVideo});
  }
  toggleFullscreen(): void {
    this.setState({fullCSS: this.state.fullCSS ? "" : " fullscreen"});
  }
  setVideoSource(srcID: string) {
    this.setState({videoSrcID: srcID});
  }
  setAudioSource(srcID: string) {
    this.setState({audioSrcID: srcID});
  }

  async leaveRoom(): Promise<void> {
    this.state.session?.disconnect();
    this.leaving = true;

    // Don't clear 'session' var here. It will cause a WelcomeMat to show
    this.setState({sessionID: "", sessionToken: ""});
    const context = this.context as GroupNavContext;
    await context.leaveHuddle();
    // no state updates after this call, component is getting torn down
  }

  onPublisherDestroyed(): boolean {
    return !this.leaving;
  }

  onRoomError(error: Error): void {
    console.error("Error from Room:", error);
    this.state.session?.disconnect();
    this.setState({
      errMsg: error.message,
      sessionID: "",
      sessionToken: "",
      session: null
    });
  }

  render(): React.ReactElement<any, any> {
    return <div className={"huddle-container attached-both-sides" + this.state.fullCSS}>
      <div className="huddle-inner-container">
        {this.state.session ?
          <>
            <SessionComponent
              session={this.state.session}
              token={this.state.sessionToken}
              sendAudio={this.state.enableAudio}
              sendVideo={this.state.enableVideo}
              isFullscreen={this.state.fullCSS != ""}
              videoSrcID={this.state.videoSrcID}
              audioSrcID={this.state.audioSrcID}
              onToggleAudio={this.toggleAudio}
              onToggleVideo={this.toggleVideo}
              onToggleFullscreen={this.toggleFullscreen}
              onLeaveRoom={this.leaveRoom}
              onPublisherDestroyed={this.onPublisherDestroyed}
              setVideoSourceID={this.setVideoSource}
              setAudioSourceID={this.setAudioSource} />
            <HuddleBlocker
              when={this.state.session !== null }
              rejectNext={[new RegExp("/group/(browse)|(threads)|(newDM)")]}
              allowedNext={[new RegExp("/group/.*")]}
              title="Confirm Navigation"
              message="Navigating away will terminate your huddle session. Are you sure?"
              leaveRoom={this.leaveRoom}/>

          </> :
          <WelcomeMatComponent
            groupMembership={this.props.groupMembership}
            onJoin={this.joinRoom}
            sendAudio={this.state.enableAudio}
            sendVideo={this.state.enableVideo}
            hideSessionOptions={this.state.sessionID !== ""}
            isWaiting={false}
            meetingHasStarted={false}
            onToggleAudio={this.toggleAudio}
            onToggleVideo={this.toggleVideo}
            onClose={this.leaveRoom} />}
      </div>
      <ErrorSnackBar errMsg={this.state.errMsg} didClose={() => this.setState({errMsg: ""})}/>
    </div>;
  }
}

interface HuddleBlockerProps {
  when: boolean,
  rejectNext?: RegExp[],
  allowedNext: RegExp[],
  title: string,
  message: string,
  leaveRoom(): Promise<void>,
}
function HuddleBlocker(props: HuddleBlockerProps): React.ReactElement<any, any> | null {
  const blocker = useBlocker(({ nextLocation }) => {
    console.log("huddle blocker nextLocation:", nextLocation.pathname,
      "allowedNext:", props.allowedNext);
    if (props.rejectNext) {
      for (const regex of props.rejectNext) {
        if (regex.test(nextLocation.pathname)) { return true; }
      }
    }
    for (const regex of props.allowedNext) {
      if (regex.test(nextLocation.pathname)) { return false; }
    }
    return true;
  });

  async function navigateAway() {
    if (!blocker) { return; }
    await props.leaveRoom();
    blocker.proceed && blocker.proceed();
  }

  console.log("huddle blocker state:", blocker.state);

  return blocker.state === "blocked" ?
    <Dialog open={true}
        title={props.title}
        cancelText="Cancel"
        confirmText="Proceed"
        loading={false}
        onClose={() => blocker.reset()}
        onConfirm={() => navigateAway()}>
      <p>{props.message}</p>
    </Dialog> : null;
}
